import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';

import { useCategories } from '@/api/category/use-categories.ts';
import { useCreateTransfer } from '@/api/transfer/use-create-transfer.ts';
import { type CreateTransferDto, CreateTransferDtoTypeEnum } from '@/api/types';
import FormInput from '@/components/form/form-input/form-input.tsx';
import FormSelect from '@/components/form/form-select/form-select.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import { parsePrice } from '@/helpers/formatters.ts';

const addTransferSchema = z.object({
  rawParty: z.string().min(1, 'Party is required'),
  amount: z
    .string()
    .min(1, 'Amount is required')
    .refine((value) => Number(value) > 0, 'Amount must be greater than 0'),
  date: z.string().min(1, 'Date is required'),
  type: z.enum(CreateTransferDtoTypeEnum),
  description: z.string().optional(),
  categoryId: z.string().optional(),
});

type AddTransferFormValues = {
  rawParty: string;
  amount: string;
  date: string;
  type: CreateTransferDtoTypeEnum;
  description?: string;
  categoryId?: string;
};

type AddTransferModalProps = {
  onClose: () => void;
};

const AddTransferModal = ({ onClose }: AddTransferModalProps) => {
  const { projectId } = useParams();

  const { data: categories } = useCategories(projectId);

  const form = useForm<AddTransferFormValues>({
    resolver: zodResolver(addTransferSchema),
    defaultValues: {
      date: dayjs().format('YYYY-MM-DD'),
      type: CreateTransferDtoTypeEnum.Debit,
    },
  });

  const { mutateAsync: createTransfer, isPending } = useCreateTransfer();

  const onSubmit = (data: AddTransferFormValues) => {
    const payload: CreateTransferDto = {
      ...data,
      amount: parsePrice(Number(data.amount)),
      date: dayjs(data.date).toISOString(),
      categoryId: data.categoryId || undefined,
    };

    createTransfer(payload)
      .then(() => {
        form.reset({
          date: dayjs().format('YYYY-MM-DD'),
          type: CreateTransferDtoTypeEnum.Debit,
        });
        form.setFocus('rawParty');
      })
      .catch(() => undefined);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <FormProvider {...form}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Transfer</DialogTitle>
            <DialogDescription>
              Enter the details of the transfer. The form stays open after each save so you can keep
              adding transfers one after another using just the keyboard.
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              form.handleSubmit(onSubmit)(event);
            }}
          >
            <FormInput control={form.control} name="rawParty" label="Party" autoFocus />
            <div className="flex gap-3">
              <FormInput
                control={form.control}
                name="amount"
                label="Amount"
                type="number"
                step="0.01"
              />
              <FormInput control={form.control} name="date" label="Date" type="date" />
            </div>
            <FormSelect
              control={form.control}
              name="type"
              label="Type"
              options={[
                { value: CreateTransferDtoTypeEnum.Debit, label: 'Debit' },
                { value: CreateTransferDtoTypeEnum.Credit, label: 'Credit' },
              ]}
            />
            <FormInput
              control={form.control}
              name="description"
              label="Description"
              placeholder="e.g. Mrkva, Hrach"
            />
            <FormSelect
              control={form.control}
              name="categoryId"
              label="Category"
              options={[
                { value: '', label: 'No category' },
                ...(categories?.map((category) => ({
                  value: category.uuid,
                  label: category.name,
                })) || []),
              ]}
            />
            <button type="submit" className="hidden" aria-hidden />
          </form>
          <DialogFooter>
            <Button onClick={() => onClose()} variant="outline">
              Done
            </Button>
            <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type="submit">
              Add transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};

export default AddTransferModal;
